/**
 * VAVCI · Recomendaciones en el carrito («Comprados juntos» + «Te puede gustar»).
 *
 * Este módulo se carga en el navegador desde snippets/vavci-cart-recs.liquid, pero
 * la lógica de selección es pura y se testea en Node (scripts/__tests__/cart-recs.test.js).
 * Por eso el arranque del DOM está guardado al final del archivo.
 */

/**
 * Selecciona qué productos mostrar en cada tira.
 * - Excluye los productos ya en el carrito.
 * - «related» no repite ids ya presentes en «complementary».
 * - Recorta a `limit` DESPUÉS de excluir, conservando el orden del servidor.
 * @param {{complementary: Item[], related: Item[], cartIds: string[], limit: number}} input
 * @returns {{complementary: Item[], related: Item[]}}
 * @typedef {{id: string, html: string}} Item
 */
export function pickRecommendations({ complementary, related, cartIds, limit }) {
  const inCart = new Set((cartIds || []).map(String));
  const comp = [];
  const seen = new Set();
  for (const item of complementary || []) {
    if (comp.length >= limit) break;
    if (inCart.has(item.id) || seen.has(item.id)) continue;
    seen.add(item.id);
    comp.push(item);
  }
  const rel = [];
  for (const item of related || []) {
    if (rel.length >= limit) break;
    if (inCart.has(item.id) || seen.has(item.id)) continue;
    seen.add(item.id);
    rel.push(item);
  }
  return { complementary: comp, related: rel };
}

/**
 * Replica el criterio de `assets/cart-drawer.js#updateStickyState()` de Horizon para
 * decidir si el resumen del carrito debe quedar `sticky` (pegado abajo) o `static`.
 * Horizon compara la altura del resumen contra la del `<dialog>` del drawer: si el
 * resumen ocupa más de la mitad, deja de pegarlo (ratio > threshold → 'false').
 * @param {number} summaryHeight
 * @param {number} drawerHeight
 * @param {number} [threshold]
 * @returns {'true'|'false'}
 */
export function summaryStickyValue(summaryHeight, drawerHeight, threshold = 0.5) {
  const ratio = drawerHeight > 0 ? summaryHeight / drawerHeight : 0;
  return drawerHeight > 0 && ratio > threshold ? 'false' : 'true';
}

// ─── Navegador ────────────────────────────────────────────────────────────────
// Diseño:
//  · No se pide nada hasta que el contenedor es visible (drawer con [open] o /cart).
//  · Una petición por semilla e intent (caché en memoria de las tarjetas crudas);
//    la exclusión por carrito se recalcula en cada sync con pickRecommendations.
//  · Horizon morphea el carrito en sitio: el servidor devuelve las tiras vacías y
//    sin nuestro data-vc-ready → un MutationObserver (rAF-debounced) reinyecta
//    desde caché sin red si la semilla no cambió.
//  · Sin eval ni handlers inline; el HTML inyectado lo renderiza Shopify (mismo origen).

const CONTAINER = '[data-vc-cart-recs]';
const READY = 'data-vc-ready';
const cache = new Map();     // productId → { complementary: Item[], related: Item[] }
const inflight = new Map();  // productId → { promise, controller }

function parseCards(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('.vcart-card')).map((node) => ({
    id: String(node.getAttribute('data-product-id') || ''),
    html: node.outerHTML,
  })).filter((x) => /^\d+$/.test(x.id));
}

async function fetchIntent(base, section, productId, limit, intent, signal) {
  const url = new URL(base, window.location.origin);
  if (url.origin !== window.location.origin) return []; // nunca seguir un data-url que apunte a otro origen
  url.search = new URLSearchParams({ section_id: section, product_id: productId, limit: String(limit), intent }).toString();
  const res = await fetch(url.toString(), { signal, headers: { Accept: 'text/html' }, credentials: 'same-origin' });
  if (!res.ok) return [];
  return parseCards(await res.text());
}

function loadSeed(el, productId) {
  if (cache.has(productId)) return Promise.resolve(cache.get(productId));
  if (inflight.has(productId)) return inflight.get(productId).promise;
  const controller = new AbortController();
  const base = el.getAttribute('data-url') || '/recommendations/products';
  const section = el.getAttribute('data-section') || 'vavci-cart-recs';
  const limit = Math.max(1, parseInt(el.getAttribute('data-limit') || '4', 10) || 4);
  // Se piden `limit * 2` para que la exclusión (carrito / dedupe) no deje la tira corta.
  const ask = limit * 2;
  const promise = Promise.all([
    fetchIntent(base, section, productId, ask, 'complementary', controller.signal),
    fetchIntent(base, section, productId, ask, 'related', controller.signal),
  ]).then(([complementary, related]) => {
    const data = { complementary, related };
    cache.set(productId, data);
    return data;
  }).catch((err) => {
    if (err && err.name === 'AbortError') return null;
    console.warn('[vavci-cart-recs] recomendaciones no disponibles:', err);
    const empty = { complementary: [], related: [] };
    // Fallo real (offline/DNS/etc.), no abortada: se cachea como un éxito vacío para no
    // reintentar en cada morph durante el resto de la página; una recarga sí reintenta.
    cache.set(productId, empty);
    return empty;
  }).finally(() => inflight.delete(productId));
  inflight.set(productId, { promise, controller });
  return promise;
}

function abortOthers(keep) {
  for (const [id, entry] of inflight) if (id !== keep) entry.controller.abort();
}

// Firma de lo último renderizado (semilla · carrito · límite): permite a sync() detectar
// cambios reales (p. ej. data-cart-ids tras añadir un producto con la misma semilla) sin
// depender de que el morph borre el atributo data-vc-ready por completo.
function readySignature(el, productId) {
  const cartIdsAttr = el.getAttribute('data-cart-ids') || '';
  const limit = Math.max(1, parseInt(el.getAttribute('data-limit') || '4', 10) || 4);
  return `${productId}|${cartIdsAttr}|${limit}`;
}

function render(el, data) {
  const limit = Math.max(1, parseInt(el.getAttribute('data-limit') || '4', 10) || 4);
  const cartIds = (el.getAttribute('data-cart-ids') || '').split(',').map((s) => s.trim()).filter(Boolean);
  const picked = pickRecommendations({ complementary: data.complementary, related: data.related, cartIds, limit });
  let any = false;
  for (const slot of ['complementary', 'related']) {
    const sec = el.querySelector(`[data-vc-recs-slot="${slot}"]`);
    if (!sec) continue;
    const strip = sec.querySelector('.vcart-recs__strip');
    const items = picked[slot];
    if (!strip || items.length === 0) { sec.hidden = true; if (strip) strip.innerHTML = ''; continue; }
    strip.innerHTML = items.map((x) => x.html).join('');
    strip.querySelectorAll('.vcart-card').forEach((c) => c.setAttribute('role', 'listitem'));
    sec.hidden = false;
    any = true;
  }
  el.hidden = !any;
  const productId = el.getAttribute('data-product-id') || '';
  // data-vc-ready no está en attributeFilter del observer (solo 'open'/'data-product-id'/
  // 'data-cart-ids'), así que esta escritura no puede retroalimentar al propio observer.
  el.setAttribute(READY, readySignature(el, productId));
  refreshSummarySticky(el);
}

// Horizon calcula `cart-summary-sticky` al abrir el drawer antes de que el morph
// inserte `.cart-drawer__summary` (primera apertura tras añadir desde carrito
// vacío: el resumen todavía no existe, sale 'false' y nunca se recalcula) y no lo
// vuelve a evaluar tras el morph. Lo reevaluamos aquí con su mismo criterio.
function refreshSummarySticky(el) {
  const dialog = el.closest('dialog');
  if (!dialog) return;
  if (!(dialog.open || dialog.closest?.('theme-drawer[open]'))) return;
  const summary = dialog.querySelector('.cart-drawer__summary');
  if (!summary) return;
  dialog.setAttribute(
    'cart-summary-sticky',
    summaryStickyValue(summary.getBoundingClientRect().height, dialog.getBoundingClientRect().height),
  );
}

function isVisible(el) {
  // Si Horizon renombra `theme-drawer`, closest() no encuentra nada y el código
  // anterior devolvía `true` a ciegas (fallaba ABIERTO → pedía en cada carga de
  // página aunque el drawer estuviera cerrado). `dialog` es el elemento nativo con
  // `open` real dentro del drawer, así que sirve de red de seguridad.
  const d = el.closest('theme-drawer, dialog');
  if (d) return d.hasAttribute('open') || d.open === true;
  return true; // página /cart
}

function sync(el) {
  if (!isVisible(el)) return;
  const productId = el.getAttribute('data-product-id') || '';
  if (!/^\d+$/.test(productId)) {
    if (el.getAttribute(READY) !== '') { el.hidden = true; el.setAttribute(READY, ''); }
    return;
  }
  const sig = readySignature(el, productId);
  if (el.getAttribute(READY) === sig) return; // misma semilla·carrito·límite ya renderizados
  abortOthers(productId);
  const cached = cache.get(productId);
  if (cached) { render(el, cached); return; }
  loadSeed(el, productId).then((data) => {
    if (!data) return; // abortada: otra semilla tomó el relevo
    // El nodo puede haber sido morpheado mientras tanto → re-localizar por semilla. Puede
    // rellenar un contenedor todavía no visible: no dispara red, solo aplica caché (pre-warm).
    document.querySelectorAll(CONTAINER).forEach((node) => {
      if (node.getAttribute('data-product-id') === productId && node.getAttribute(READY) !== readySignature(node, productId)) render(node, data);
    });
  });
}

function syncAll() {
  document.querySelectorAll(CONTAINER).forEach(sync);
}

let scheduled = false;
// debounce con setTimeout(0), no rAF: en pestañas ocultas rAF no dispara y dejaría `scheduled` atascado
function schedule() {
  if (scheduled) return;
  scheduled = true;
  setTimeout(() => { scheduled = false; syncAll(); }, 0);
}

// Horizon no resetea el scroll del carrito al reabrir (el <theme-drawer> lleva
// `no-persist`, pero eso es el atributo del propio drawer, no el scrollTop de
// sus contenedores internos). Con el bloque de recomendaciones dentro del mismo
// scroller que los productos, sin este reset el cliente reabría el carrito con
// el producto añadido fuera de vista si había llegado a hacer scroll hasta las
// recomendaciones antes de cerrarlo.
function resetItemsScroll(target) {
  target.querySelectorAll('.cart-drawer__items').forEach((s) => { s.scrollTop = 0; });
}

function init() {
  syncAll();
  // childList: morph/re-render del carrito; attributes[open]: apertura del drawer.
  const mo = new MutationObserver((records) => {
    for (const record of records) {
      if (record.attributeName === 'open' && record.target.hasAttribute('open') && record.target.matches?.('theme-drawer')) {
        resetItemsScroll(record.target);
        // Reabrir/primera apertura: recalculamos aquí también porque, si el
        // contenedor ya estaba en caché, render() no se vuelve a ejecutar.
        record.target.querySelectorAll(CONTAINER).forEach(refreshSummarySticky);
      }
    }
    schedule();
  });
  mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'data-product-id', 'data-cart-ids'] });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
